/**
 * Core Enums & Primitives - Exact match to backend Pydantic v2 schemas
 * Backend: app/game/schemas.py, app/game/constants.py
 */

// Color - Player perspective
export type Color = 'white' | 'black';

// PieceType - Algebraic notation single character
export type PieceType = 'P' | 'N' | 'B' | 'R' | 'Q' | 'K';

// Square - Algebraic notation (e.g., 'e4', 'a1', 'h8')
export type Square = `${File}${Rank}`;

export type File = 'a' | 'b' | 'c' | 'd' | 'e' | 'f' | 'g' | 'h';
export type Rank = '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8';

// UCI move format (e.g., 'e2e4', 'e7e8q')
export type UCI = `${Square}${Square}` | `${Square}${Square}${PromotionSuffix}`;
export type PromotionSuffix = 'q' | 'r' | 'b' | 'n';

// MatchStatus - Game lifecycle state machine
// Backend: MatchStatus enum (WAITING_PLAYERS, SETUP, SETUP_LOCKED, IN_PROGRESS, FINISHED)
export type MatchStatus =
  | 'WAITING_PLAYERS'
  | 'SETUP'
  | 'SETUP_LOCKED'
  | 'IN_PROGRESS'
  | 'FINISHED';

// EndgameType - Termination reason
// Backend: EndgameType enum
export type EndgameType =
  | 'checkmate'
  | 'stalemate'
  | 'insufficient_material'
  | 'threefold_repetition'
  | 'fifty_move_rule'
  | 'resignation'
  | 'disconnection_timeout'
  | 'turn_timeout'
  | 'fivefold_repetition'
  | 'seventy_five_move_rule'
  | 'draw_agreement'
  | 'draw_claim';

// OpponentKind - Match opponent type
// Backend: OpponentKind enum
export type OpponentKind = 'human' | 'ai';

// AIDifficulty - Bot difficulty tiers
// Backend: AIDifficulty enum (EASY, MEDIUM, HARD)
export type AIDifficulty = 'easy' | 'medium' | 'hard';

// QueueStage - PvP matchmaking stages (FTPES tiers)
// Backend: QueueStage enum (STAGE_1 through STAGE_5)
export type QueueStage = 'stage_1' | 'stage_2' | 'stage_3' | 'stage_4' | 'stage_5';

// FogViewMode - Replay view perspective
// Backend: ViewMode enum
export type FogViewMode = 'white' | 'black' | 'truth';

// RoomMode - Friend room type
// Backend: RoomMode enum
export type RoomMode = 'match' | 'tournament';

// RoomStatus - Friend room lifecycle
// Backend: RoomStatus enum
export type RoomStatus = 'waiting' | 'ready' | 'started';

// SeatIndex - Tournament bracket seat (0-7)
// Backend: SeatIndex (int 0-7)
export type SeatIndex = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

// TournamentNodeStatus - Bracket node state
// Backend: TournamentNodeStatus enum
export type TournamentNodeStatus = 'pending' | 'ready' | 'in_progress' | 'finished' | 'bye';

// TicketStatus - Ticket economy state
// Backend: TicketStatus enum
export type TicketStatus = 'free' | 'reserved';

// PublicTournamentRound - Public tournament round (1-3)
// Backend: PublicTournamentRound enum
export type PublicTournamentRound = 1 | 2 | 3;

// PublicTournamentStatus - Public tournament run state
// Backend: PublicTournamentStatus enum
export type PublicTournamentStatus = 'in_progress' | 'champion' | 'runner_up' | 'eliminated';

// FeatureFlag - Dynamic feature toggles
// Backend: FeatureFlag keys
export type FeatureFlag =
  | 'ai_enabled'
  | 'pvp_enabled'
  | 'replays_enabled'
  | 'analytics_outbox_enabled'
  | 'tickets_enabled'
  | 'rooms_enabled'
  | 'tournaments_enabled'
  | 'spectating_enabled';

// RateLimitEvent - WebSocket rate limiting events
// Backend: RateLimitEvent enum
export type RateLimitEvent =
  | 'move:submit'
  | 'room:create'
  | 'room:join'
  | 'room:leave'
  | 'room:pick_seat'
  | 'room:kick'
  | 'room:start'
  | 'room:state'
  | 'spectate:join'
  | 'spectate:leave'
  | 'tournament:ready'
  | 'queue:join'
  | 'queue:leave'
  | 'reconnect:attempt';

// TokenType - JWT token type
export type TokenType = 'bearer';

// AuthErrorCode - Authentication error codes
export type AuthErrorCode =
  | 'invalid_credentials'
  | 'username_taken'
  | 'token_expired'
  | 'token_invalid'
  | 'refresh_expired'
  | 'refresh_invalid'
  | 'refresh_revoked';

// GameErrorCode - Game-specific error codes (error:* events)
export type GameErrorCode =
  | 'server_rebooting'
  | 'invalid_payload'
  | 'unknown_match'
  | 'not_in_match'
  | 'not_authenticated'
  | 'wrong_phase'
  | 'feature_disabled'
  | 'service_unavailable'
  | 'gate_locked'
  | 'already_queued'
  | 'not_joined'
  | 'no_active_match'
  | 'lock_busy'
  | 'corrupted_state'
  | 'not_your_turn'
  | 'internal'
  | 'not_found'
  | 'not_room_owner'
  | 'room_not_ready'
  | 'not_room_member'
  | 'cannot_spectate_own_match'
  | 'spectate_not_available';

// FTPES Stage - Progression tiers
// Backend: Stage enum (1-5)
export type FTPESStage = 1 | 2 | 3 | 4 | 5;

// Rating bounds per stage
export const STAGE_BOUNDS: Record<FTPESStage, [number, number]> = {
  1: [0, 499],
  2: [500, 999],
  3: [1000, 1499],
  4: [1500, 1999],
  5: [2000, Infinity],
} as const;

// Win/Loss deltas per stage (Phase 7+ recalibrated)
export const STAGE_DELTAS: Record<FTPESStage, { win: number; loss: number }> = {
  1: { win: 20, loss: -5 },
  2: { win: 15, loss: -10 },
  3: { win: 10, loss: -10 },
  4: { win: 10, loss: -15 },
  5: { win: 5, loss: -20 },
} as const;

// Gate thresholds
export const GATE_THRESHOLDS = {
  PVP: 200,
  HARD_AI: 700,
} as const;

// Tactical bonus cap
export const TACTICAL_BONUS_CAP = 15;

// Timer constants (seconds)
export const TIMER_CONSTANTS = {
  SETUP: 90,
  TURN: 60,
  RECONNECT_GRACE: 60,
  MATCHMAKING_TIMEOUT: 15,
  SWEEP_INTERVAL: 2,
  COOLDOWN_24H: 86400000,
} as const;

// Lock TTL
export const LOCK_TTL_MS = 3000;

// Match state TTL
export const MATCH_STATE_TTL_SECONDS = 3600;

// Scored flag TTL
export const SCORED_FLAG_TTL_SECONDS = 604800;

// Ticket economy
export const TICKET_CAP = 3;
export const TICKET_RESERVATION_TTL_SECONDS = 300;

// Room lobby TTL
export const ROOM_LOBBY_TTL_SECONDS = 600;

// Tournament ready TTL
export const TOURNAMENT_READY_TTL_SECONDS = 300;

// Health probe cache
export const HEALTH_READYZ_CACHE_TTL_SECONDS = 3;
export const FEATURE_FLAG_L1_TTL_SECONDS = 30;

// WebSocket mount path
export const WS_MOUNT_PATH = '/ws';

// API version prefix
export const API_PREFIX = '/api/v1';

// Color utility functions
export const oppositeColor = (color: Color): Color => (color === 'white' ? 'black' : 'white');

export const colorFromTurn = (turn: Color): Color => turn;

export function isValidSquare(square: string): square is Square {
  return /^[a-h][1-8]$/.test(square);
}

export function isValidUCI(uci: string): uci is UCI {
  return /^[a-h][1-8][a-h][1-8]([qrbn])?$/.test(uci);
}

export function parseUCI(uci: UCI): { from: Square; to: Square; promotion?: PromotionSuffix } {
  const from = uci.slice(0, 2) as Square;
  const to = uci.slice(2, 4) as Square;
  if (uci.length === 5) {
    return { from, to, promotion: uci[4] as PromotionSuffix };
  }
  return { from, to };
}

export function squareToIndex(square: Square): number {
  const file = square.charCodeAt(0) - 97;
  const rank = 8 - parseInt(square[1]!, 10);
  return rank * 8 + file;
}

export function indexToSquare(index: number): Square {
  const file = String.fromCharCode(97 + (index % 8));
  const rank = String.fromCharCode(56 - Math.floor(index / 8));
  return `${file}${rank}` as Square;
}

export function squareFromCoords(file: number, rank: number): Square {
  return `${String.fromCharCode(97 + file)}${8 - rank}` as Square;
}

export function coordsFromSquare(square: Square): { file: number; rank: number } {
  return {
    file: square.charCodeAt(0) - 97,
    rank: 8 - parseInt(square[1]!, 10),
  };
}

// Piece type utilities
export const PIECE_VALUES: Record<PieceType, number> = {
  P: 1,
  N: 3,
  B: 3,
  R: 5,
  Q: 9,
  K: 100,
} as const;

export const PIECE_SYMBOLS_WHITE: Record<PieceType, string> = {
  P: '♙',
  N: '♘',
  B: '♗',
  R: '♖',
  Q: '♕',
  K: '♔',
} as const;

export const PIECE_SYMBOLS_BLACK: Record<PieceType, string> = {
  P: '♟',
  N: '♞',
  B: '♝',
  R: '♜',
  Q: '♛',
  K: '♚',
} as const;

export function pieceSymbol(type: PieceType, color: Color): string {
  return color === 'white' ? PIECE_SYMBOLS_WHITE[type] : PIECE_SYMBOLS_BLACK[type];
}

// Stage utilities
export function stageFromScore(score: number): FTPESStage {
  if (score >= 2000) return 5;
  if (score >= 1500) return 4;
  if (score >= 1000) return 3;
  if (score >= 500) return 2;
  return 1;
}

export function stageToQueueStage(stage: FTPESStage): QueueStage {
  return `stage_${stage}` as QueueStage;
}

export function queueStageToStage(queueStage: QueueStage): FTPESStage {
  return parseInt(queueStage.split('_')[1]!, 10) as FTPESStage;
}

export function canJoinPvP(score: number): boolean {
  return score >= GATE_THRESHOLDS.PVP;
}

export function canPlayHardAI(score: number): boolean {
  return score >= GATE_THRESHOLDS.HARD_AI;
}

export function getAIDifficultyForStage(stage: FTPESStage): AIDifficulty {
  if (stage === 1) return 'easy';
  if (stage === 2) return 'medium';
  return 'hard';
}

// Timer utilities
export function isSetupPhase(status: MatchStatus): boolean {
  return status === 'SETUP' || status === 'SETUP_LOCKED';
}

export function isInProgress(status: MatchStatus): boolean {
  return status === 'IN_PROGRESS';
}

export function isFinished(status: MatchStatus): boolean {
  return status === 'FINISHED';
}

// Endgame type utilities
export function isDrawType(type: EndgameType): boolean {
  return [
    'stalemate',
    'insufficient_material',
    'threefold_repetition',
    'fifty_move_rule',
    'fivefold_repetition',
    'seventy_five_move_rule',
    'draw_agreement',
    'draw_claim',
  ].includes(type);
}

export function isDecisiveType(type: EndgameType): boolean {
  return !isDrawType(type);
}

export function winnerColorFromResult(type: EndgameType, winnerColor?: Color): Color | null {
  if (isDrawType(type)) return null;
  return winnerColor ?? null;
}

// Constants
export const INITIAL_FEN = 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1' as const;

export const BACK_RANKS: Record<Color, Rank[]> = {
  white: ['1', '2'],
  black: ['7', '8'],
} as const;

export const KING_START: Record<Color, Square> = {
  white: 'e1',
  black: 'e8',
} as const;

export const KING_SHIELD: Record<Color, Square> = {
  white: 'e2',
  black: 'e7',
} as const;

export const ALL_SQUARES: Square[] = [
  'a1', 'b1', 'c1', 'd1', 'e1', 'f1', 'g1', 'h1',
  'a2', 'b2', 'c2', 'd2', 'e2', 'f2', 'g2', 'h2',
  'a3', 'b3', 'c3', 'd3', 'e3', 'f3', 'g3', 'h3',
  'a4', 'b4', 'c4', 'd4', 'e4', 'f4', 'g4', 'h4',
  'a5', 'b5', 'c5', 'd5', 'e5', 'f5', 'g5', 'h5',
  'a6', 'b6', 'c6', 'd6', 'e6', 'f6', 'g6', 'h6',
  'a7', 'b7', 'c7', 'd7', 'e7', 'f7', 'g7', 'h7',
  'a8', 'b8', 'c8', 'd8', 'e8', 'f8', 'g8', 'h8',
] as const;

export const FILES: File[] = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;
export const RANKS: Rank[] = ['1', '2', '3', '4', '5', '6', '7', '8'] as const;

// Socket connection lifecycle state
export type ConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'error';

// Socket configuration
export interface SocketConfig {
  url: string;
  auth: { token: string };
  autoConnect: boolean;
  reconnection: boolean;
  reconnectionAttempts: number;
  reconnectionDelay: number;
  reconnectionDelayMax: number;
  timeout: number;
  transports: ('websocket' | 'polling')[];
}

// Default socket configuration
export const DEFAULT_SOCKET_CONFIG: SocketConfig = {
  url: '',
  auth: { token: '' },
  autoConnect: false,
  reconnection: true,
  reconnectionAttempts: 10,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 5000,
  timeout: 20000,
  transports: ['websocket', 'polling'],
};